import {Box, Scroll} from 'platform/foundation';

import {useCallback, useLayoutEffect, useRef} from 'react';

import {isNotNil} from 'ramda';

import {useDebouncedCallback} from 'shared/web';

import {UseDocumentPreviewReturnType} from '../types/UseDocumentPreviewReturnType';
import {DocumentPreviewSlideshowItem} from './DocumentPreviewSlideshowItem';

interface DocumentPreviewSlideshowProps {
  documentPreview: UseDocumentPreviewReturnType;
}

export function DocumentPreviewSlideshow(props: DocumentPreviewSlideshowProps) {
  const slideshowRef = useRef<HTMLDivElement>(null);
  const slideIndexInViewRef = useRef<number>(0);
  const isNavigatingRef = useRef<boolean>(false);
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useLayoutEffect(() => {
    const isNotInView = props.documentPreview.state.activeIndex !== slideIndexInViewRef.current;
    const targetElement = slideshowRef.current?.children?.[props.documentPreview.state.activeIndex];

    if (isNotInView && isNotNil(targetElement)) {
      isNavigatingRef.current = true;

      // Clear any existing timeout to prevent race conditions
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }

      targetElement.scrollIntoView({
        behavior: 'instant',
        block: 'start',
      });

      slideIndexInViewRef.current = props.documentPreview.state.activeIndex;

      /**
       * Prevent visibility callbacks during navigation to avoid thumbnail highlighting issues
       * in landscape mode when displaying 2 pages simultaneously.
       */
      navigationTimeoutRef.current = setTimeout(() => {
        isNavigatingRef.current = false;
        navigationTimeoutRef.current = null;
      }, NAVIGATION_SETTLE_DELAY);
    }

    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, [props.documentPreview.state.activeIndex]);

  const handleScroll = useCallback(() => {
    if (props.documentPreview.state.activeIndex === slideIndexInViewRef.current) {
      return;
    }
    props.documentPreview.controls.setActiveIndex(slideIndexInViewRef.current);
  }, [props.documentPreview.state.activeIndex, props.documentPreview.controls]);

  const onSlideshowScroll = useDebouncedCallback(handleScroll, DEBOUNCE_DELAY, DEBOUNCE_WAIT);

  const onSlideshowItemInView = (index: number) => {
    if (isNavigatingRef.current) {
      return;
    }
    slideIndexInViewRef.current = index;
  };

  return (
    <Scroll
      auto
      hasHiddenScrollBars
      width="100%"
      height="100%"
      onScroll={(event) => {
        event?.preventDefault();
        onSlideshowScroll();
      }}
    >
      <Box ref={slideshowRef} height="100%" width="100%" paddingHorizontal={4}>
        {props.documentPreview.state.pages.map((documentPage, documentPageIndex) => (
          <DocumentPreviewSlideshowItem
            key={documentPage.id}
            documentPage={documentPage}
            documentPreview={props.documentPreview}
            onSlideshowItemInView={() => onSlideshowItemInView(documentPageIndex)}
          />
        ))}
      </Box>
    </Scroll>
  );
}

const DEBOUNCE_DELAY = 200;
const DEBOUNCE_WAIT = 3000;
const NAVIGATION_SETTLE_DELAY = 300;
